import React from 'react';
// React Router 不会把错误通过 props 传入 errorElement，需用 useRouteError 从路由上下文获取
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useRouteError } from 'react-router';

interface FallbackComponentProps {
  error?: Error;
  reset?: () => void;
}

/**
 * 将 useRouteError() 可能返回的各种类型（Error、字符串、ErrorResponse 等）统一转为可展示的字符串，
 * 避免 [object Object] 或渲染报错。
 */
function errorToDisplayString(value: unknown): string {
  if (value instanceof Error) {
    return value.stack ? `${value.message}\n${value.stack}` : value.message;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value !== null && typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const FallbackComponent: React.FC<FallbackComponentProps> = ({
  error: errorProp,
  reset,
}) => {
  const { t } = useTranslation();
  const routeError = useRouteError();
  // 从路由上下文取错（路由 errorElement 场景）；props.error 保留以兼容非路由用法
  const effectiveError = routeError ?? errorProp;
  const errorText = isRouteErrorResponse(effectiveError)
    ? `${effectiveError.status} ${effectiveError.statusText}: ${errorToDisplayString(effectiveError.data ?? 'no body')}`
    : effectiveError !== undefined && effectiveError !== null
      ? errorToDisplayString(effectiveError)
      : '';

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>{t('error_boundary.title', 'Something went wrong')}</h2>
      <p>
        {t(
          'error_boundary.description',
          'Sorry, an error occurred while loading the page.',
        )}
      </p>
      {/* 仅当有可展示的错误内容时才渲染 Error details，避免空白块 */}
      {errorText ? (
        <details style={{ whiteSpace: 'pre-wrap', marginTop: '16px' }}>
          <summary>{t('error_boundary.details', 'Error details')}</summary>
          {errorText}
        </details>
      ) : null}
      <div style={{ marginTop: '16px' }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginRight: '12px',
            padding: '8px 16px',
            backgroundColor: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {t('error_boundary.reload', 'Reload Page')}
        </button>
        {reset && (
          <button
            onClick={reset}
            style={{
              padding: '8px 16px',
              backgroundColor: '#52c41a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {t('error_boundary.retry', 'Retry')}
          </button>
        )}
      </div>
    </div>
  );
};

export default FallbackComponent;
